import { HierarchyUtils, ComponentIds, ValidationResultType, SnapUtils } from '@inter-ikea-core/core';
import { OutlinedComponent } from '@inter-ikea-core/core';
import InvalidPlacementComponent from '../components/InvalidPlacementComponent';
import { AlbedoColor, OutlineColor } from '../SceneApi';
import { PlannerEcsMultiSystem } from './System';
import EventComponent from '../components/EventComponent';
import RoomChangeComponent from '../components/RoomChangeComponent';
import InteriorComponent from '../components/InteriorComponent';
/**
 * System for validating frame placement
 */
export class InvalidFramePlacementSystem extends PlannerEcsMultiSystem {
    constructor(coreApi) {
        super(coreApi, 'InvalidFramePlacementSystem', [
            RoomChangeComponent.id,
            ComponentIds.OUTLINED_COMPONENT_ID,
            ComponentIds.SELECTED_COMPONENT_ID,
            ComponentIds.IS_LOADING_SNAPSHOT_COMPONENT_ID,
            InvalidPlacementComponent.id,
            EventComponent.id
        ]);
    }
    enable() {
        this.isEnabled = true;
        this.validateFramePlacements();
    }
    onAdd(entity, newComponent) {
        if (newComponent.typeId === InvalidPlacementComponent.id) {
            this.onInvalidPlacement(entity);
        }
        else if (newComponent.typeId === ComponentIds.SELECTED_COMPONENT_ID) {
            if (!entity.hasComponent(InvalidPlacementComponent.id) &&
                !this.coreApi.scene?.selectionSystemDisabled) {
                this.coreApi.scene?.setGlobalOutlineColor(OutlineColor.BLUE);
            }
            const invalidEntities = this.coreApi.entities.getByComponent(InvalidPlacementComponent.id);
            invalidEntities.forEach(entity => {
                entity.removeComponent(ComponentIds.OUTLINED_COMPONENT_ID);
            });
        }
    }
    onUpdate(entity, oldComponent, newComponent) {
        if (this.coreApi.entities.getByComponent(ComponentIds.IS_DRAGGED_COMPONENT_ID).length > 0) {
            return;
        }
        if (newComponent.typeId === RoomChangeComponent.id) {
            this.validateFramePlacements();
        }
        else if (newComponent.typeId === EventComponent.id &&
            newComponent.event === 'history') {
            this.validateFramePlacements();
        }
    }
    onRemove(entity, oldComponent) {
        if (oldComponent.typeId === InvalidPlacementComponent.id) {
            this.onPlacementCorrected(entity);
        }
        else if (oldComponent.typeId === ComponentIds.OUTLINED_COMPONENT_ID) {
            // Counteract Core's selection/outline systems
            const isPlacementInvalid = entity.hasComponent(InvalidPlacementComponent.id);
            const isSelected = entity.hasComponent(ComponentIds.SELECTED_COMPONENT_ID);
            if (isPlacementInvalid && isSelected) {
                entity.addComponent(new OutlinedComponent());
            }
        }
        else if (oldComponent.typeId === ComponentIds.SELECTED_COMPONENT_ID) {
            const invalidEntities = this.coreApi.entities.getByComponent(InvalidPlacementComponent.id);
            if (invalidEntities.length > 0) {
                this.coreApi.scene?.setGlobalOutlineColor(OutlineColor.RED);
                invalidEntities.forEach(entity => {
                    entity.addComponent(new OutlinedComponent());
                });
            }
        }
        else if (oldComponent.typeId === ComponentIds.IS_LOADING_SNAPSHOT_COMPONENT_ID) {
            this.coreApi
                .getServices()
                .sceneManager.entities.forEach(entity => this.onPlacementCorrected(entity));
            this.validateFramePlacements();
        }
    }
    /**
     * Runs when InvalidPlacementComponent is added to an entity
     */
    onInvalidPlacement(entity) {
        entity.addComponent(new OutlinedComponent());
        this.coreApi.scene?.setGlobalOutlineColor(OutlineColor.RED);
        const objectId = this.coreApi.entities.getObjectId(entity);
        if (objectId) {
            this.coreApi.scene?.setObjectBaseColor(AlbedoColor.RED, objectId);
        }
    }
    /**
     * Runs when InvalidPlacementComponent is remove from an entity
     */
    onPlacementCorrected(entity) {
        entity.removeComponent(ComponentIds.OUTLINED_COMPONENT_ID);
        const objectId = this.coreApi.entities.getObjectId(entity);
        if (objectId) {
            this.coreApi.scene?.setObjectBaseColor(AlbedoColor.NONE, objectId);
        }
        const invalidEntities = this.coreApi.entities.getByComponent(InvalidPlacementComponent.id);
        if (invalidEntities.length === 0) {
            // After removing the invalid placement component, reset the outline of all frame and interiors in the scene
            const frames = this.coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
            const interiors = this.coreApi.entities.getByComponent(InteriorComponent.id);
            const framesAndInteriors = [...frames, ...interiors];
            this.coreApi.entities.getObjectIds(framesAndInteriors).forEach(id => {
                this.coreApi.scene?.setObjectBaseColor(AlbedoColor.NONE, id);
            });
        }
    }
    /**
     * Check if frame placement is correct.
     * Adds InvalidPlacementComponent to entities that are incorrecly placed
     */
    validateFramePlacements() {
        const scene = this.coreApi.getServices().sceneManager.scene;
        if (!scene)
            return;
        const sceneChildren = HierarchyUtils.getDescendantsWithComponent(scene, ComponentIds.FRAME_COMPONENT_ID);
        if (!sceneChildren)
            return;
        const invalidEntities = sceneChildren
            .flatMap(child => {
            // Check if all children to the room is valid placed
            const invalidConnection = this.coreApi
                .getServices()
                .validations.validateCurrentState(child)
                .filter(result => result.resultType === ValidationResultType.FAIL);
            return invalidConnection.length > 0 ? child : [];
        })
            .filter(f => !f.hasComponent(ComponentIds.ROOM_PART_COMPONENT_ID));
        // Remove InvalidPlacementComponent from previously invalid frames.
        this.coreApi.entities.getByComponent(InvalidPlacementComponent.id).forEach(frame => {
            frame.removeComponent(InvalidPlacementComponent.id);
        });
        if (invalidEntities.length > 0) {
            this.coreApi.entities.unselect();
            invalidEntities.forEach(entity => {
                // We need to make sure we get the root of the frame, since it can be part of an assembly
                if (entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
                    // get root of product if part of assemblies
                    const root = SnapUtils.snapRoot(entity);
                    root?.addComponent(new InvalidPlacementComponent());
                }
                // For interiors we just add the InvalidPlacementComponent directly, as they can be placed incorrectly but the frame could still be correct.
                if (entity.hasComponent(InteriorComponent.id)) {
                    entity?.addComponent(new InvalidPlacementComponent());
                }
            });
        }
    }
}
//# sourceMappingURL=InvalidFramePlacementSystem.js.map